import { createContext, Fragment, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import type { Me } from "@gioia365/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useRouteContext, useRouterState } from "@tanstack/react-router";
import { useMe } from "../../api/queries";
import { useT, type Translate } from "../../i18n/locale";
import { AccountDrawer } from "../../routes/account-drawer";
import { railItemsOf, type RailItem } from "../../routes/rail-items";
import { initialsInTab, menuSubjectOf, nameInTab, useOptionalAccess, useOptionalTab, type Access } from "../../session/access";
import {
  canUseCommercialView,
  CommercialViewBand,
  CommercialViewClosed,
  CommercialViewGate,
  CommercialViewSelector,
} from "../../shell/CommercialViewSelector";
import { CompanySwitcher } from "../../shell/CompanySwitcher";
import { useRailMode } from "../../shell/rail-mode";
import { DialerBar } from "../../dialer/DialerBar";
import { DialerSessionProvider } from "../../dialer/dialer-session";
import { DueNowBanner } from "../../tasks/DueNowBanner";
import { CallBar } from "../../telephony/CallBar";
import { CallSessionProvider } from "../../telephony/call-session";
import { Icon } from "./Icon";
import { Reason } from "./Reason";
import "./app-shell.css";

// Il guscio dell'applicazione (piano M1b Task 5, dal mock-up `renderShell` con la rail bianca della proposta B):
// «Vai al contenuto», barra in alto sotto i 720 px con «Menu», rail con le voci di `RAIL_ITEMS` e, in fondo,
// il pulsante che apre «Il tuo account»; sopra il contenuto l'avviso fisso del contesto di piattaforma (D13).
// Vale nelle pagine di un'azienda e in quelle della piattaforma, dove la scheda può non avere un'azienda.
// «Guarda come…», la Vista Commerciale (piano M2 Task 22): il selettore si apre dalla rail, sopra il pulsante
// dell'account; durante la vista la fascia sta in cima al contenuto, fissa come l'avviso di D13, e la pagina rinasce a
// ogni cambio di vista.
// Una voce con un sottomenu (`flyout`, piano M3 Task 8: le Smart List) lo mostra sotto di sé con la rail aperta e fissa e
// nel pannello del menu sul telefono (lì anche nella sua sezione); il sottomenu segue la voce nell'ordine di tabulazione.
// Nella sua sezione la voce non è la pagina corrente (`aria-current` sta sulla voce aperta del sottomenu) ma resta
// evidenziata. Fuori dal contesto di piattaforma il sottomenu è sempre nella rail, nascosto e inerte finché la rail è a
// icone: aprirlo non aggiunge nulla sopra le voci che seguono fra la pressione e il rilascio di un clic (end-to-end della
// CI, 2 ottobre 2026, quando la rail si apriva anche al passaggio del puntatore).
// Durante la vista la rail è il menu della persona scelta, di qualunque ruolo (decisioni del product owner del 2 ottobre
// 2026): le stesse voci calcolate sui suoi permessi, mai quelle della piattaforma; restano la voce della vista e
// l'account. Una pagina fuori dal suo menu mostra l'avviso al posto del contenuto (`CommercialViewGate`). Uscendo, torna
// subito il menu di chi guarda.
// Su uno schermo largo, da 1280 px, la rail è aperta e fissa (riscontro del product owner del 3 ottobre 2026, questione
// aperta 183, `shell/rail-mode.ts`): etichette e sottomenu sempre in vista, il contenuto le sta accanto. «Riduci il menu»
// la chiude e «Allarga il menu» la apre (decisione del product owner del 4 ottobre 2026): ridotta, la rail resta a icone
// anche con il puntatore o il focus dentro, a ogni larghezza da desktop, e non si apre più sopra la pagina; il focus resta
// sul pulsante. Ogni icona tiene il suo nome accessibile e, a icone, il tooltip nativo con la sua etichetta (`title`).
// Il pulsante c'è da 1200 px; sotto, la rail resta a icone (questione aperta 271), e sul telefono c'è il pannello di «Menu».
// Con la rail fissa il sottomenu è in vista e letto anche nel contesto di piattaforma: il product owner lavora sempre
// come Super admin di Gioia365 dentro un'azienda, e con l'eccezione di D13 le liste comparivano soltanto passando sulla voce
// (riscontro del 3 ottobre 2026 sullo staging). Ogni lettura resta nel registro dell'azienda, come ogni sua richiesta: il
// server la scrive per richiesta. Con la rail a icone le liste non ci sono; nel pannello del telefono l'eccezione resta.
// La chiamata della scheda vive qui (piano M4, Task 13, D10): `CallSessionProvider` avvolge il guscio, quindi cambiare
// pagina non la interrompe, e le barre del guscio (`ShellBars`), sotto gli avvisi e sopra la pagina, la mostrano fuori dalla
// scheda del Lead chiamato. Dentro la chiamata vive la sessione del Power Dialer (piano M5, Task 5: `DialerSessionProvider`),
// che ne osserva le fasi e fa partire le chiamate della cascata; la sua barra (`DialerBar`) sta sopra quella della chiamata.
// Sopra tutte e due, su ogni pagina, il banner del richiamo concordato con l'ora arrivata (piano M5, Task 20; D14).
// Una voce può avere un numero accanto (`RailItem.count`, piano M6, R3, passo 12b; D24: i WhatsApp da leggere accanto a
// «Conversazioni»): si vede con la rail aperta e fissa, con la rail a icone (sull'icona) e nel pannello del telefono, e le sue parole
// sono la descrizione della voce, non il suo nome.

const InsideAppShell = createContext(false);

/** Vero dentro il guscio: la pagina d'errore non ci aggiunge un'altra cornice. */
export function useInsideAppShell(): boolean {
  return useContext(InsideAppShell);
}

/** Gli identificativi da mettere in `aria-describedby` di una voce (il segno sull'icona, il numero), oppure niente. */
function describedBy(...ids: (string | null)[]): string | undefined {
  const present = ids.filter((id): id is string => id !== null);
  return present.length > 0 ? present.join(" ") : undefined;
}

/** La seconda riga del pulsante dell'account: ruolo e azienda della scheda, o il Super admin di Gioia365. */
function roleLine(t: Translate, me: Me, access: Access | null): string | null {
  if (access?.isPlatform) return t("shell.account.platformIn", { organization: access.context.organization.name });
  if (access) return t("shell.account.role", { role: access.context.role?.name ?? "", organization: access.context.organization.name });
  return me.isPlatformAdmin ? t("shell.account.platform") : null;
}

export function AppShell({ children }: { children: ReactNode }) {
  return (
    <CallSessionProvider>
      <DialerSessionProvider>
        <Shell>{children}</Shell>
      </DialerSessionProvider>
    </CallSessionProvider>
  );
}

/**
 * Le barre del guscio, nel contenuto sotto gli avvisi (il contesto di piattaforma, la fascia di «Guarda come…») e sopra la
 * pagina, fisse in cima con loro. Una sotto l'altra: per primo il banner del richiamo concordato con l'ora arrivata (M5, Task 20,
 * `DueNowBanner`, mai nel contesto di piattaforma), poi la barra del Power Dialer (M5, `DialerBar`), poi quella della chiamata.
 * Una barra che non ha niente da dire non occupa spazio.
 */
function ShellBars() {
  return (
    <div className="g-shell__bars">
      <DueNowBanner />
      <DialerBar />
      <CallBar />
    </div>
  );
}

function Shell({ children }: { children: ReactNode }) {
  const t = useT();
  const me = useMe();
  const access = useOptionalAccess();
  const tab = useOptionalTab();
  const view = tab?.commercialView ?? null;
  // Il ruolo della persona osservata, dal contesto della vista (lo stesso da cui viene il suo menu).
  const observed = access?.context.commercialView ?? null;
  const observedRole = view && observed?.membershipId === view.membershipId ? observed.role.name : null;
  const canView = access !== null && canUseCommercialView(access);
  const [viewOpen, setViewOpen] = useState(false);
  const railId = useId();
  const mainId = useId();
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);
  const railRef = useRef<HTMLElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const tabStorage = useRouteContext({ from: "/autenticato/guscio", select: (context) => context.tabStorage });
  const rail = useRailMode();

  // Telefono: il pannello aperto prende il focus sulla prima voce; Esc lo chiude e il focus torna a «Menu».
  // Il listener sta qui e non nel JSX perché `<nav>` non è un controllo.
  useEffect(() => {
    const rail = railRef.current;
    if (!menuOpen || !rail) return;
    rail.querySelector<HTMLElement>("a[href], button")?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      menuRef.current?.focus();
    };
    rail.addEventListener("keydown", handleKeyDown);
    return () => rail.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  function closeMenu() {
    setMenuOpen(false);
    menuRef.current?.focus();
  }

  // Scelta una voce, il focus passa al contenuto: il pannello del telefono si chiude e chi usa uno screen reader si
  // trova nella pagina nuova, non su una voce del pannello ormai chiuso. Sempre la stessa funzione: il sottomenu di una
  // voce non si ridisegna per lei.
  const choose = useCallback(() => {
    setMenuOpen(false);
    mainRef.current?.focus({ preventScroll: true });
  }, []);

  const name = nameInTab(me, access);
  const initials = initialsInTab(me, access);
  const role = roleLine(t, me, access);
  // La rail a icone su desktop: le etichette non si vedono, e ogni icona ha il tooltip nativo con la sua (`title`).
  const iconsOnly = rail.desktop && !rail.pinned;
  const tooltip = (label: string) => (iconsOnly ? label : undefined);
  // Il menu della scheda: di chi guarda, oppure, con «Guarda come…», della persona scelta.
  const menu = useMemo(() => menuSubjectOf(me, access, tab), [me, access, tab]);
  const items = useMemo(() => railItemsOf(menu), [menu]);
  const queryClient = useQueryClient();

  // Appena la scheda ha il contesto, le voci della rail leggono in anticipo i dati delle loro schermate (`prefetch`):
  // il cambio di sezione li trova già nella cache. Di nuovo al passaggio sulla voce, se nel frattempo sono vecchi.
  // La scheda porta la Vista Commerciale: i dati letti in anticipo sono quelli della vista.
  // Nel contesto di piattaforma niente lettura all'apertura: ogni sua richiesta scrive una riga nel registro dell'azienda
  // (D13), e il Super admin di Gioia365 che apre soltanto le Impostazioni ne lascerebbe una sull'elenco dei Lead che non
  // ha guardato. Restano il passaggio del puntatore e il focus sulla voce, che dicono che sta per aprirla. Soltanto le voci
  // del menu: con «Guarda come…» quelle della persona scelta.
  useEffect(() => {
    if (!access || !tab || access.isPlatform) return;
    for (const item of items) if (item.prefetch) item.prefetch({ queryClient, access, tab, tabStorage });
  }, [access, tab, items, queryClient, tabStorage]);

  function prefetch(item: RailItem) {
    if (access && tab && item.prefetch) item.prefetch({ queryClient, access, tab, tabStorage });
  }

  return (
    <InsideAppShell.Provider value={true}>
      <div className="g-app">
        {/* Il link resta un link vero; il clic porta il focus al contenuto senza cambiare l'indirizzo. */}
        <a
          className="g-skip"
          href={`#${mainId}`}
          onClick={(event) => {
            event.preventDefault();
            mainRef.current?.focus();
          }}
        >
          {t("shell.skipToContent")}
        </a>
        <header className="g-topbar">
          <button
            ref={menuRef}
            type="button"
            className="g-topbar__menu"
            aria-expanded={menuOpen}
            aria-controls={railId}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <Icon name="menu" size="md" />
            <span className="g-visually-hidden">{t("shell.menu")}</span>
          </button>
          <span className="g-topbar__brand">
            <span className="g-topbar__mark" aria-hidden="true" />
            {t("common.appName")}
          </span>
        </header>
        <div className="g-app__layout" data-rail={rail.pinned ? "pinned" : "icons"}>
          {/* Aperta e fissa (`data-pinned`) oppure a icone, e a icone resta chiusa: nessuno stato di puntatore o focus. */}
          <nav
            id={railId}
            ref={railRef}
            className="g-rail"
            aria-label={t("shell.mainNavigation")}
            data-open={String(menuOpen)}
            data-pinned={String(rail.pinned)}
          >
            <div className="g-rail__panel">
              <span className="g-rail__brand">
                <span className="g-rail__mark" aria-hidden="true" />
                <span className="g-rail__name">{t("common.appName")}</span>
              </span>
              {/* L'azienda della scheda e, per chi ne ha più di una, il menu per cambiarla (3 ottobre 2026). */}
              <CompanySwitcher onChoose={choose} iconsOnly={iconsOnly} />
              <ul className="g-nav">
                {items.map((item) => {
                  if (item.flyout) {
                    return (
                      <FlyoutItem
                        key={item.id}
                        item={item}
                        flyout={item.flyout}
                        flyoutId={`${railId}-${item.id}`}
                        indicatorId={`${railId}-${item.id}-indicator`}
                        countId={`${railId}-${item.id}-count`}
                        drawerOpen={menuOpen}
                        iconsOnly={iconsOnly}
                        pinned={rail.pinned}
                        platform={access?.isPlatform === true}
                        onChoose={choose}
                        onPrefetch={() => prefetch(item)}
                      />
                    );
                  }
                  const indicatorId = `${railId}-${item.id}-indicator`;
                  const countId = `${railId}-${item.id}-count`;
                  const Indicator = iconsOnly ? item.indicator : undefined;
                  const Count = item.count;
                  return (
                    <li key={item.id} className="g-nav__entry">
                      <Link
                        to={item.to}
                        className="g-nav__item"
                        title={tooltip(t(item.label))}
                        aria-describedby={describedBy(Indicator ? indicatorId : null, Count ? countId : null)}
                        onClick={choose}
                        onPointerEnter={() => prefetch(item)}
                        onFocus={() => prefetch(item)}
                      >
                        <Icon name={item.icon} size="md" />
                        <span className="g-nav__label">{t(item.label)}</span>
                      </Link>
                      {Indicator ? <Indicator id={indicatorId} /> : null}
                      {Count ? <Count id={countId} /> : null}
                    </li>
                  );
                })}
              </ul>
              <div className="g-rail__footer">
                {canView ? (
                  <button
                    type="button"
                    className="g-nav__item g-rail__view"
                    aria-haspopup="dialog"
                    title={tooltip(t("shell.commercialView.open"))}
                    data-active={view ? "true" : undefined}
                    onClick={() => {
                      setMenuOpen(false);
                      setViewOpen(true);
                    }}
                  >
                    <Icon name="eye" size="md" />
                    <span className="g-nav__label">{t("shell.commercialView.open")}</span>
                  </button>
                ) : null}
                <button
                  type="button"
                  className="g-rail__user"
                  aria-haspopup="dialog"
                  title={tooltip([name, role].filter(Boolean).join("\n"))}
                  onClick={() => setAccountOpen(true)}
                >
                  <span className="g-avatar" aria-hidden="true">{initials}</span>
                  <span className="g-rail__user-text">
                    <span className="g-visually-hidden">{t("account.title")}</span>
                    <strong>{name}</strong>
                    {role ? <span>{role}</span> : null}
                  </span>
                </button>
                {/* Aperta e fissa, oppure a icone: dove la rail fissa lascia alla pagina lo spazio che le serve (da 1200 px). */}
                {rail.canPin ? (
                  <button
                    type="button"
                    className="g-nav__item g-rail__toggle"
                    aria-expanded={rail.pinned}
                    aria-controls={railId}
                    title={tooltip(t("shell.rail.expand"))}
                    onClick={() => rail.toggle()}
                  >
                    <Icon name={rail.pinned ? "collapse" : "expand"} size="md" />
                    <span className="g-nav__label">{t(rail.pinned ? "shell.rail.collapse" : "shell.rail.expand")}</span>
                  </button>
                ) : null}
              </div>
            </div>
          </nav>
          {/* Il fondo scuro dietro il pannello del telefono: un clic chiude, come Esc (che vale anche da tastiera). */}
          <div className="g-rail-backdrop" role="presentation" data-open={String(menuOpen)} onClick={closeMenu} />
          <main id={mainId} ref={mainRef} className="g-app__main" tabIndex={-1}>
            {/* Gli avvisi e le barre: senza niente da mostrare il riquadro non occupa spazio (`app-shell.css`). */}
            <div className="g-app__notices">
              {access?.isPlatform ? (
                <Reason tone="warning" className="g-platform-notice" role="note">
                  {t("shell.platformNotice", { organization: access.context.organization.name })}
                </Reason>
              ) : null}
              {view ? <CommercialViewBand view={view} role={observedRole} canChange={canView} onChange={() => setViewOpen(true)} /> : null}
              <ShellBars />
            </div>
            <CommercialViewClosed />
            {/* Una vista nuova è una pagina nuova: niente righe, selezioni o finestre aperte della vista di prima. */}
            <Fragment key={view?.membershipId ?? ""}>
              <CommercialViewGate view={view} subject={menu}>
                {children}
              </CommercialViewGate>
            </Fragment>
          </main>
        </div>
        <AccountDrawer open={accountOpen} onClose={() => setAccountOpen(false)} />
        {viewOpen && tab && canView ? <CommercialViewSelector tab={tab} onClose={() => setViewOpen(false)} /> : null}
      </div>
    </InsideAppShell.Provider>
  );
}

type FlyoutItemProps = {
  item: RailItem;
  flyout: NonNullable<RailItem["flyout"]>;
  flyoutId: string;
  /** L'elemento con le parole del segno sull'icona (`RailItem.indicator`), la descrizione della voce. */
  indicatorId: string;
  /** L'elemento con le parole del numero accanto alla voce (`RailItem.count`), anche lui nella descrizione. */
  countId: string;
  /** Il pannello del menu del telefono è aperto. */
  drawerOpen: boolean;
  /**
   * La rail è a icone su desktop: il sottomenu resta chiuso (la voce apre l'ultima lista aperta, o la prima), l'icona ha
   * il segno delle novità e il tooltip con l'etichetta. Aperta e fissa le novità le dice già il sottomenu, e sul telefono
   * anche (le liste sono nel pannello di «Menu»).
   */
  iconsOnly: boolean;
  /**
   * La rail è aperta e fissa: il sottomenu è sempre aperto, anche nel contesto di piattaforma.
   */
  pinned: boolean;
  /**
   * Il contesto di piattaforma: nel pannello del telefono il sottomenu si apre soltanto quando la voce lo chiede (sezione,
   * puntatore o focus sulla voce), perché ogni sua lettura resta nel registro dell'azienda (D13); soltanto allora entra
   * nella pagina, e con la rail a icone mai. Con la rail fissa è sempre aperto, come fuori dal contesto di piattaforma
   * (riscontro del product owner del 3 ottobre 2026).
   */
  platform: boolean;
  onChoose: () => void;
  onPrefetch: () => void;
};

/**
 * Una voce della rail con il sottomenu. Segue l'indirizzo da sé, e non il guscio: il guscio ridisegnato a ogni
 * navigazione rileggerebbe le query della scheda proprio mentre una guardia le toglie (cambio di azienda, scheda
 * rifiutata). Il sottomenu è lo stesso elemento finché `onChoose` e l'apertura non cambiano, quindi un cambio di
 * indirizzo dentro o fuori dalla sezione ridisegna la voce ma non le sue liste.
 *
 * Fuori dal contesto di piattaforma il sottomenu resta nella pagina anche chiuso (vedi in cima al file): chiuso, è nascosto
 * agli screen reader e fuori dal focus (`aria-hidden`, `inert`) e il CSS della rail a icone lo toglie dalla vista; `open`
 * gli dice quando leggere, così la lettura parte all'apertura. Con la rail a icone su desktop resta chiuso anche con il
 * puntatore o il focus sulla voce (decisione del product owner del 4 ottobre 2026), e la voce non dice di potersi aprire.
 */
function FlyoutItem({ item, flyout: Flyout, flyoutId, indicatorId, countId, drawerOpen, iconsOnly, pinned, platform, onChoose, onPrefetch }: FlyoutItemProps) {
  const t = useT();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // Il puntatore sopra la voce o il focus dentro di lei, sottomenu compreso: contano soltanto nel pannello del telefono.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const section = pathname === item.to || pathname.startsWith(`${item.to}/`);
  const expanded = !iconsOnly && (pinned || section || hovered || focused || (!platform && drawerOpen));
  const mounted = expanded || !platform;
  const flyout = useMemo(() => <Flyout open={expanded} onNavigate={onChoose} />, [Flyout, expanded, onChoose]);
  const Indicator = iconsOnly ? item.indicator : undefined;
  const Count = item.count;
  return (
    <li
      className="g-nav__entry"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      {/* Nella sua sezione la voce resta evidenziata, ma la pagina corrente è la voce aperta del sottomenu. */}
      <Link
        to={item.to}
        className="g-nav__item"
        activeOptions={{ exact: true }}
        data-section={section ? "true" : undefined}
        title={iconsOnly ? t(item.label) : undefined}
        aria-expanded={iconsOnly ? undefined : expanded}
        aria-controls={expanded ? flyoutId : undefined}
        aria-describedby={describedBy(Indicator ? indicatorId : null, Count ? countId : null)}
        onClick={onChoose}
        onPointerEnter={onPrefetch}
        onFocus={onPrefetch}
      >
        <Icon name={item.icon} size="md" />
        <span className="g-nav__label">{t(item.label)}</span>
      </Link>
      {Indicator ? <Indicator id={indicatorId} /> : null}
      {Count ? <Count id={countId} /> : null}
      {mounted ? (
        <div id={flyoutId} className="g-nav__flyout" aria-hidden={expanded ? undefined : true} inert={!expanded}>
          {flyout}
        </div>
      ) : null}
    </li>
  );
}
